import { Component, Input, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';

export type TextareaSize = 'xs' | 'sm' | 'md' | 'lg';

@Component({
  selector: 'app-textarea',
  standalone: true,
  imports: [CommonModule, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextareaComponent),
      multi: true,
    },
  ],
  template: `
    <div class="w-full">
      <!-- Label -->
      <label
        *ngIf="label"
        [for]="textareaId"
        class="block text-sm font-medium text-gray-700 mb-2"
      >
        {{ label }}
        <span *ngIf="required" class="text-red-500">*</span>
      </label>

      <!-- Textarea -->
      <textarea
        [id]="textareaId"
        [placeholder]="placeholder"
        [disabled]="disabled"
        [readonly]="readonly"
        [required]="required"
        [rows]="rows"
        [maxlength]="maxlength"
        [(ngModel)]="value"
        (blur)="onTouched()"
        (input)="onInput($event)"
        [ngClass]="getTextareaClasses()"
        [attr.aria-label]="ariaLabel || label"
        [attr.aria-describedby]="error || hint ? textareaId + '-description' : null"
        [attr.aria-invalid]="error ? 'true' : null"
      ></textarea>

      <!-- Helper Text / Error -->
      <div
        *ngIf="error || hint"
        [id]="textareaId + '-description'"
        class="mt-1 text-sm"
        [ngClass]="{
          'text-red-500': error,
          'text-gray-500': !error && hint
        }"
      >
        {{ error || hint }}
      </div>

      <!-- Character Count -->
      <div *ngIf="maxlength && showCharCount" class="mt-1 text-xs text-gray-500 text-right">
        {{ value?.length || 0 }} / {{ maxlength }}
      </div>
    </div>
  `,
})
export class TextareaComponent implements ControlValueAccessor {
  @Input() label?: string;
  @Input() placeholder: string = '';
  @Input() hint?: string;
  @Input() error?: string;
  @Input() size: TextareaSize = 'md';
  @Input() disabled: boolean = false;
  @Input() readonly: boolean = false;
  @Input() required: boolean = false;
  @Input() rows: number = 4;
  @Input() maxlength?: number;
  @Input() showCharCount: boolean = false;
  @Input() ariaLabel?: string;
  @Input() textareaId: string = `textarea-${Math.random().toString(36).substr(2, 9)}`;
  @Input() className: string = '';

  value: any = '';
  onChange: any = () => {};
  onTouched: any = () => {};

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInput(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    this.value = textarea.value;
    this.onChange(this.value);
  }

  getTextareaClasses(): string {
    const baseClasses =
      'w-full border rounded-lg transition-colors focus:outline-none focus:ring-2 focus:border-transparent resize-y';

    const sizeClasses = {
      xs: 'px-2 py-1 text-xs',
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-3 py-2 text-base',
      lg: 'px-4 py-3 text-lg',
    };

    const stateClasses = this.error
      ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
      : 'border-gray-300 focus:ring-[#4A90E2] focus:ring-[#4A90E2]';

    const disabledClasses = this.disabled
      ? 'bg-gray-100 cursor-not-allowed opacity-60'
      : 'bg-white';

    const readonlyClasses = this.readonly
      ? 'bg-gray-50 cursor-default'
      : '';

    return `${baseClasses} ${sizeClasses[this.size]} ${stateClasses} ${disabledClasses} ${readonlyClasses} ${this.className}`.trim();
  }
}
